import { faCopy, faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import {
    faCopy as faCopySolid,
    faPen as faPenSolid,
    faTrash as faTrashSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { IAssemblyApplicationResponse } from '@kcftech/prometheus-client';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { Tag } from 'components/tag/Tag';
import { HighlightedText } from 'components/text/HighlightedText';
import { usePermissions } from 'contexts/PermissionsContext';
import {
    AssemblyApplicationActions,
    AssemblyApplicationBar,
    AssemblyApplicationLabel,
} from 'pages/Settings/AssemblyType/styled';
import { DebugValue } from 'shared/debug/DebugValue';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

type ApplicationLabelProps = Readonly<{
    $isSelected: boolean;
}>;

const ApplicationLabel = styled.span<ApplicationLabelProps>`
    cursor: pointer;
    padding: 0.5rem;
    align-items: center;
    background: ${({ $isSelected }) =>
        $isSelected ? tokens.action.selected : 'transparent'};
`;

export type AssemblyApplicationProps = Readonly<{
    application: IAssemblyApplicationResponse;
    isSelected: boolean;
    filterText: string;
    onSelect: () => void;
    onApplicationEditClick: () => void;
    onApplicationDeleteClick: () => void;
    onApplicationDuplicateClick: () => void;
}>;

export function AssemblyApplication({
    application,
    isSelected,
    filterText,
    onSelect,
    onApplicationEditClick,
    onApplicationDeleteClick,
    onApplicationDuplicateClick,
}: AssemblyApplicationProps) {
    const { canEditAssemblyType } = usePermissions();

    const parameterCount = application.ParametersDefinition?.Data
        ? Object.keys(application.ParametersDefinition.Data).length
        : 0;

    return (
        <AssemblyApplicationBar className="f-14">
            <AssemblyApplicationLabel>
                <ApplicationLabel $isSelected={isSelected} onClick={() => onSelect()}>
                    <HighlightedText text={application.Name} terms={[filterText]} />
                </ApplicationLabel>
                <DebugValue value={application} description="Assembly Application" />
                {parameterCount > 0 && (
                    <span title={`${parameterCount} parameters`} className="ml-10">
                        <Tag
                            style={{
                                backgroundColor: tokens.action.selected,
                                height: '2rem',
                            }}
                        >
                            {parameterCount}
                        </Tag>
                    </span>
                )}
            </AssemblyApplicationLabel>
            {canEditAssemblyType && (
                <AssemblyApplicationActions>
                    <HoverIconButton
                        title={`Edit ${application.Name}`}
                        ariaLabel={`edit ${application.Name} application button`}
                        icon={faPen}
                        hoverIcon={faPenSolid}
                        color={tokens.icon.actionActive}
                        hoverColor={tokens.icon.actionHover}
                        size="14"
                        onClick={(event) => {
                            onApplicationEditClick();
                            event.stopPropagation();
                        }}
                    />
                    <HoverIconButton
                        title={`Duplicate ${application.Name}`}
                        ariaLabel={`duplicate ${application.Name} application button`}
                        icon={faCopy}
                        hoverIcon={faCopySolid}
                        color={tokens.icon.actionActive}
                        hoverColor={tokens.icon.actionHover}
                        size="14"
                        onClick={(event) => {
                            onApplicationDuplicateClick();
                            event.stopPropagation();
                        }}
                    />
                    <HoverIconButton
                        title={`Delete ${application.Name}`}
                        ariaLabel={`delete ${application.Name} application button`}
                        icon={faTrash}
                        hoverIcon={faTrashSolid}
                        color={tokens.icon.actionActive}
                        hoverColor={tokens.error.default}
                        size="14"
                        onClick={(event) => {
                            onApplicationDeleteClick();
                            event.stopPropagation();
                        }}
                    />
                </AssemblyApplicationActions>
            )}
        </AssemblyApplicationBar>
    );
}
